import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import './ZoneList.css';

/**
 * 区域列表组件
 * 显示所有可用的区域及其描述和统计数据
 * 移动端专用版本
 * 
 * @returns {JSX.Element} 区域列表组件
 */
const ZoneList = () => {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 加载区域数据
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoading(true);
        
        // 如果API服务器未就绪，使用静态数据
        const staticZones = [
          {
            id: 'zone1',
            name: '区域一',
            description: '北京地区羽毛球数据',
            icon: '🏙️',
            color: '#4c6ef5',
            stats: {
              matchCount: 128,
              playerCount: 36,
              eventCount: 12
            }
          },
          {
            id: 'zone2',
            name: '区域二',
            description: '上海地区羽毛球数据',
            icon: '🌃',
            color: '#37b24d',
            stats: {
              matchCount: 85,
              playerCount: 28,
              eventCount: 8
            }
          }
        ];
        
        try {
          const apiBase = getApiBaseUrl();
          const response = await axios.get(`${apiBase}/api/zones`);
          
          // 修改对响应数据的处理，适应实际API格式
          if (response.data.status === 'success' && response.data.data && response.data.data.zones) {
            // 获取每个区域的统计数据
            const zonesWithStats = await Promise.all(
              response.data.data.zones.map(async (zone) => {
                // 处理zone对象，确保id使用_id或code
                const zoneId = zone.code || (zone.id ? zone.id : zone._id);
                
                try {
                  const apiBase = getApiBaseUrl();
                  const statsResponse = await axios.get(
                    `${apiBase}/api/zones/${zoneId}/stats`
                  );
                  
                  if (statsResponse.data.status === 'success' && statsResponse.data.data) {
                    return {
                      id: zoneId,
                      name: zone.name,
                      description: zone.description || '',
                      icon: zone.icon || '🏸',
                      color: zone.color || '#4285F4',
                      active: zone.active,
                      stats: statsResponse.data.data.stats || {
                        matchCount: 0,
                        playerCount: 0,
                        eventCount: 0
                      }
                    };
                  }
                  
                  return {
                    id: zoneId,
                    name: zone.name,
                    description: zone.description || '',
                    icon: zone.icon || '🏸',
                    color: zone.color || '#4285F4',
                    active: zone.active
                  };
                } catch (err) {
                  console.error(`获取区域 ${zoneId} 统计数据失败:`, err);
                  
                  // 返回区域基本信息，无统计数据
                  return {
                    id: zoneId,
                    name: zone.name,
                    description: zone.description || '',
                    icon: zone.icon || '🏸',
                    color: zone.color || '#4285F4',
                    active: zone.active
                  };
                }
              })
            );
            
            setZones(zonesWithStats);
          } else {
            // 如果响应格式不符合预期，使用静态数据
            console.warn('API响应格式不符合预期:', response.data);
            setZones(staticZones);
          }
        } catch (err) {
          console.error('获取区域列表失败:', err);
          // 失败时使用静态数据
          setZones(staticZones);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchZones();
  }, []);
  
  const handleZoneClick = (zoneId) => {
    navigate(`/badminton/${zoneId}`);
  };
  
  // 渲染加载状态
  if (loading) {
    return (
      <div className="zone-list-container">
        <div className="zone-list-loading">
          <div className="loading-spinner"></div>
          <p>加载区域数据中...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="zone-list-container">
      <div className="zone-list-header">
        <h1>选择数据区域</h1>
        <p className="zone-list-subtitle">
          请选择您要查看的羽毛球数据区域
        </p>
      </div>
      
      <div className="zone-cards">
        {zones.map((zone) => (
          <div
            key={zone.id}
            className="zone-card"
            onClick={() => handleZoneClick(zone.id)}
            style={{ borderTopColor: zone.color }}
          >
            <div className="zone-icon" style={{ backgroundColor: zone.color }}>
              {zone.icon}
            </div>
            <h2 className="zone-name">{zone.name}</h2>
            <p className="zone-description">{zone.description}</p>
            <div className="zone-stats">
              <div className="stat-item">
                <span className="stat-value">
                  {zone.stats ? zone.stats.matchCount : '-'}
                </span>
                <span className="stat-label">比赛</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  {zone.stats ? zone.stats.playerCount : '-'}
                </span>
                <span className="stat-label">球员</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  {zone.stats ? zone.stats.eventCount : '-'}
                </span>
                <span className="stat-label">活动</span>
              </div>
            </div>
            <button className="zone-button">进入区域</button>
          </div>
        ))}
      </div>
      
      <div className="zone-list-footer">
        <p>您可以稍后随时切换区域</p>
      </div>
    </div>
  );
};

export default ZoneList; 